import { useCallback, useMemo, useRef, useState } from 'react'

/** Anything that can key a row: a library item id, or a `provider:external_id` string. */
export type SelectionId = string | number

export interface Selection<T extends SelectionId> {
  /** The current selection. Treat as read-only — mutate through the methods. */
  selected: Set<T>
  isSelected: (id: T) => boolean
  /** Adds or removes one id, and enters selection mode. */
  toggle: (id: T) => void
  select: (id: T) => void
  deselect: (id: T) => void
  /** Adds every id to what is already selected. `clear()` is how you reset. */
  selectAll: (ids: readonly T[]) => void
  /** Empties the selection and leaves selection mode. */
  clear: () => void
  /**
   * Shift-click behaviour: selects everything between the last touched row and
   * `id`, in the order the list is rendered. Falls back to `toggle` when there
   * is no usable anchor.
   */
  toggleRange: (id: T, orderedIds: readonly T[]) => void
  count: number
  /**
   * Whether selection mode is on. Set by the first toggle/select, cleared only
   * by `clear()` — so it can be true while `count` is 0, which is deliberate:
   * unticking the last row should not yank the toolbar out from under the user.
   */
  active: boolean
}

/**
 * Multi-select state for a list.
 *
 * Deliberately unaware of what it is selecting: pages hold one of these per list
 * and pass ids in. Every method is referentially stable, so it is safe in a
 * dependency array or a memoised row.
 */
export function useSelection<T extends SelectionId = number>(): Selection<T> {
  const [selected, setSelected] = useState<Set<T>>(() => new Set<T>())
  const [active, setActive] = useState(false)

  // The last row the user touched, for range select. A ref because changing the
  // anchor alone never changes what is rendered.
  const anchor = useRef<T | null>(null)

  const isSelected = useCallback((id: T) => selected.has(id), [selected])

  const toggle = useCallback((id: T) => {
    anchor.current = id
    setActive(true)
    setSelected((previous) => {
      const next = new Set(previous)
      if (!next.delete(id)) next.add(id)
      return next
    })
  }, [])

  const select = useCallback((id: T) => {
    anchor.current = id
    setActive(true)
    setSelected((previous) => {
      if (previous.has(id)) return previous
      const next = new Set(previous)
      next.add(id)
      return next
    })
  }, [])

  const deselect = useCallback((id: T) => {
    anchor.current = id
    setSelected((previous) => {
      if (!previous.has(id)) return previous
      const next = new Set(previous)
      next.delete(id)
      return next
    })
  }, [])

  const selectAll = useCallback((ids: readonly T[]) => {
    if (ids.length === 0) return
    anchor.current = ids[ids.length - 1]
    setActive(true)
    setSelected((previous) => {
      const next = new Set(previous)
      for (const id of ids) next.add(id)
      return next
    })
  }, [])

  const clear = useCallback(() => {
    anchor.current = null
    setActive(false)
    setSelected((previous) => (previous.size === 0 ? previous : new Set<T>()))
  }, [])

  const toggleRange = useCallback(
    (id: T, orderedIds: readonly T[]) => {
      const from = anchor.current
      const start = from === null ? -1 : orderedIds.indexOf(from)
      const end = orderedIds.indexOf(id)

      // No anchor, or the anchor is no longer in this list (filter changed,
      // page reloaded): a plain toggle is the only honest interpretation.
      if (start === -1 || end === -1) {
        toggle(id)
        return
      }

      const low = Math.min(start, end)
      const high = Math.max(start, end)

      anchor.current = id
      setActive(true)
      setSelected((previous) => {
        const next = new Set(previous)
        for (let index = low; index <= high; index += 1) next.add(orderedIds[index])
        return next
      })
    },
    [toggle],
  )

  return useMemo(
    () => ({
      selected,
      isSelected,
      toggle,
      select,
      deselect,
      selectAll,
      clear,
      toggleRange,
      count: selected.size,
      active,
    }),
    [selected, isSelected, toggle, select, deselect, selectAll, clear, toggleRange, active],
  )
}
